package com.arena_web.ui.components.navigation

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.m
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.mBottom
import com.arena_web.ui.utils.mGap
import com.arena_web.ui.utils.mHeight
import com.arena_web.ui.utils.mPadding
import com.arena_web.ui.utils.mSize
import com.arena_web.ui.utils.mWidth
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import org.jetbrains.compose.web.css.*

@Composable
fun BottomNavBar(activeTab: SidebarTab? = null) {
    val ctx = rememberPageContext()
    val sportColors = LocalSportColors.current

    // Phones drop the Chat tab (chat opens from the Arena top-bar icon);
    // desktop's SidebarNav keeps it. Order: Arena, News, People, Feed, Profile.
    val tabOrder = SidebarTab.entries.filter { it != SidebarTab.Chat }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .position(Position.Fixed)
            .mBottom(24)
            .left(0.px)
            .right(0.px)
            .zIndex(1000),
        contentAlignment = Alignment.Center
    ) {
        Row(
            modifier = Modifier
                .position(Position.Relative)
                .mBorderRadius(150)
                .mPadding(8, 6)
                .mGap(8)
                .styleModifier {
                    // Figma 10271:223391 — translucent navy fill so the blurred
                    // backdrop shows through (light: frosted white glass).
                    property("background", "var(--l-nav-bg, rgba(2, 10, 28, 0.45))")
                    property("backdrop-filter", "blur(20px)")
                    property("-webkit-backdrop-filter", "blur(20px)")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Gradient border overlay — painted on a separate absolute layer so the
            // stroke survives the parent's backdrop-filter (the border-box background
            // trick is washed out by backdrop-filter).
            Box(
                modifier = Modifier.styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.8px solid transparent")
                    property("border-radius", m(150))
                    property("background", "${AppGradients.PanelBorderGradient} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
            )

            tabOrder.forEach { tab ->
                val isActive = tab == activeTab

                NavAnchor(tab.route, onNavigate = { ctx.router.navigateTo(tab.route) }) {
                Box(
                    modifier = Modifier
                        .mSize(60)
                        .mBorderRadius(100)
                        .cursor(Cursor.Pointer)
                        .then(
                            if (isActive) Modifier.backgroundColor(sportColors.primary)
                            else Modifier
                        ),
                    contentAlignment = Alignment.Center
                ) {
                    // The Arena logo (ProPlayLogo) is a wide 1.47:1 emblem; forcing
                    // it into a 24×24 square distorts the ring. Per Figma it sits at
                    // ~30×20 (≈51%×35% of the 60px circle). Other tabs are 24×24.
                    val iconSize =
                        if (tab == SidebarTab.Arena) Modifier.mWidth(30).mHeight(20)
                        else Modifier.mSize(24)

                    // Selected → white (icon-on-primary), otherwise the themed
                    // default tint (#B4CCF6 dark / slate navy light). Both
                    // filters start with brightness(0), so they recolor any
                    // source fill (the blue ProPlay ring, the muted-blue icons).
                    Icon(
                        src = tab.icon,
                        modifier = iconSize.styleModifier {
                            property(
                                "filter",
                                if (isActive) sportColors.iconFilterOnPrimary
                                else com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V
                            )
                        }
                    )
                }
                }
            }
        }
    }
}
